import { useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";

import BookingWidget from "./BookingWidget";
import useSectionView from "../../utils/useSectionView";
import { trackWidgetEvent } from "../../utils/tracking";

// Wraps the Timely iframe as a deliberate in-page section (heading + card)
// so it reads as part of the page, not a stray embed. Timely has no public
// JS API/postMessage events, so tracking here is necessarily approximate:
// - booking_widget_view: the section scrolled into view (real signal).
// - booking_widget_focus: a window-blur heuristic — the browser blurs the
//   window when focus moves into a cross-origin iframe, so if the last
//   focused element was this iframe, the visitor almost certainly clicked
//   into it. It cannot tell us whether they completed a booking.
const BookingWidgetSection = ({ source }) => {
  const iframeContainerRef = useRef(null);
  const hasFiredFocus = useRef(false);
  const sectionRef = useSectionView(`Booking Widget - ${source}`);

  const setRefs = (node) => {
    sectionRef.current = node;
    iframeContainerRef.current = node;
  };

  useEffect(() => {
    const handleBlur = () => {
      if (hasFiredFocus.current) return;
      const iframe = iframeContainerRef.current?.querySelector("iframe");
      if (iframe && document.activeElement === iframe) {
        hasFiredFocus.current = true;
        trackWidgetEvent("focus", source);
      }
    };

    window.addEventListener("blur", handleBlur);
    return () => window.removeEventListener("blur", handleBlur);
  }, [source]);

  return (
    <Box
      component="section"
      ref={setRefs}
      aria-label="Book online"
      sx={{
        backgroundColor: "var(--color-bg-warm)",
        borderRadius: "12px",
        border: "1px solid var(--color-border)",
        mt: 6,
        p: { xs: 2.5, md: 4 },
      }}
    >
      <Stack alignItems="center" gap={1} sx={{ mb: 3, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
          }}
        >
          Book Online
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            fontSize: { xs: "24px", md: "28px" },
          }}
        >
          Prefer to Book Here?
        </Typography>
        <Typography
          sx={{
            color: "var(--color-text-light)",
            fontSize: "15px",
            maxWidth: "480px",
            lineHeight: 1.6,
            fontFamily: "var(--font-body)",
          }}
        >
          Choose your treatment and appointment time below — no need to leave
          this page.
        </Typography>
      </Stack>
      <Stack alignItems="center">
        <Box
          sx={{
            width: "100%",
            maxWidth: "420px",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(90, 77, 71, 0.08)",
            backgroundColor: "#fff",
          }}
        >
          <BookingWidget />
        </Box>
      </Stack>
    </Box>
  );
};

export default BookingWidgetSection;
